import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeRoot from '../components/ThemeRoot';
import DarkButton from '../components/DarkButton';
import { useAuthStore } from '../../stores/authStore';
import { signOut } from '../../hooks/useAuth';

const EMAIL_BLOCKLIST = /@.*\.internal$/i;

function LogoutIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const loading = useAuthStore((s) => s.loading);
  const [showConfirm, setShowConfirm] = useState(false);

  async function handleSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  // Loading state
  if (loading) {
    return (
      <ThemeRoot>
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/10 border-t-amber" />
        </div>
      </ThemeRoot>
    );
  }

  // Error state — auth finished loading but no user
  if (!user) {
    return (
      <ThemeRoot>
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="font-marker text-xl text-coral">Something went wrong</p>
          <p className="font-gill text-sm text-ink/60">
            We couldn't load your account. Please sign in again.
          </p>
          <button
            onClick={() => navigate('/auth', { replace: true })}
            className="mt-4 rounded-2xl border border-amber/40 bg-amber/10 px-6 py-2.5 font-marker text-sm text-amber transition-colors hover:bg-amber/20"
          >
            Go to Sign In
          </button>
        </div>
      </ThemeRoot>
    );
  }

  const displayName = profile?.explorerName || profile?.displayName || '';
  const avatarChar = displayName ? displayName[0].toUpperCase() : '?';
  const email = user.email ?? '';
  const showEmail = !!email && !EMAIL_BLOCKLIST.test(email);

  return (
    <ThemeRoot>
      <div className="mx-auto max-w-xl px-5 py-6">
        {/* Header */}
        <div className="mb-8 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 font-gill text-ink/70 transition-colors hover:bg-white/10"
            aria-label="Go back"
          >
            ←
          </button>
          <h1 className="font-marker text-2xl text-ink">Account</h1>
        </div>

        {/* Avatar */}
        <div className="flex flex-col items-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full border border-amber/30 bg-amber/10 font-marker text-4xl text-amber">
            {avatarChar}
          </div>
          {displayName && (
            <p className="mt-4 font-marker text-xl text-amber">{displayName}</p>
          )}
          {profile?.explorerName && (
            <p className="mt-0.5 font-gill text-sm text-ink/50">
              @{profile.explorerName}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="my-8 border-t border-white/10" />

        {/* Info rows */}
        {showEmail && (
          <div className="flex items-center justify-between gap-3 py-2.5">
            <span className="shrink-0 font-gill text-sm text-ink/50">Email</span>
            <span className="truncate text-right font-gill text-sm text-ink/80">
              {email}
            </span>
          </div>
        )}

        {/* Sign out */}
        <div className="mt-10">
          <button
            onClick={() => setShowConfirm(true)}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-coral/40 py-3.5 font-marker text-base text-coral transition-colors hover:bg-coral/10 active:scale-[0.98]"
          >
            <LogoutIcon />
            Sign out
          </button>
        </div>
      </div>

      {/* Sign-out confirmation */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConfirm(false); }}
        >
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-surface p-6 shadow-2xl">
            <h3 className="font-marker text-xl text-ink">Sign out?</h3>
            <p className="mt-2 font-gill text-sm text-ink/70">
              You will need to sign in again to use OMore.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <DarkButton accent="ghost" onClick={() => setShowConfirm(false)}>
                Cancel
              </DarkButton>
              <button
                onClick={handleSignOut}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-coral/40 px-5 py-3 font-marker text-base text-coral transition-colors hover:bg-coral/10 active:scale-[0.97]"
              >
                <LogoutIcon />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </ThemeRoot>
  );
}
